import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ModelsGalleryPageProps,
  Model,
  FilterState,
  SortOption,
} from './types';
import { modelsService } from '@/services/modelsService';
import { useWallet } from '@/contexts/WalletContext';

import ModelCard from './ModelCard';
import SearchBar from './SearchBar';
import FilterControls from './FilterControls';
import PageHeaderSkeleton from './PageHeaderSkeleton';
import SearchBarSkeleton from './SearchBarSkeleton';
import ModelCardSkeleton from './ModelCardSkeleton';
import ModelsGalleryErrorBoundary from './ModelsGalleryErrorBoundary';
import { useVirtualScrollingGrid } from '@/hooks/useVirtualScrolling';
import { usePerformanceMonitoring } from '@/utils/performanceMonitoring';
import styles from '@/styles/components/models-gallery/ModelsGalleryPage.module.css';

/**
 * Models Gallery Page component that displays a searchable, filterable grid of AI models.
 *
 * Features:
 * - Real-time search and filtering
 * - Virtual scrolling for large model lists (>50 items)
 * - Performance monitoring and optimization
 * - Keyboard navigation support
 * - Responsive design
 * - Error handling and loading states
 *
 * @param props - Component props
 * @param props.className - Optional CSS class name for styling
 */

interface LoadingState {
  isInitialLoading: boolean;
  isSearching: boolean;
  isFiltering: boolean;
  error: string | null;
}

const ModelsGalleryPage: React.FC<ModelsGalleryPageProps> = React.memo(
  ({ className }) => {
    // Use navigate hook - will be null in test environments
    const navigate = useNavigate();

    // Wallet context for blockchain integration
    const { state: walletState } = useWallet();

    // Performance monitoring
    const { measureAsync } = usePerformanceMonitoring();

    // Refs for keyboard navigation and virtual scrolling
    const gridRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [focusedModelIndex, setFocusedModelIndex] = useState<number>(-1);
    const [containerHeight, setContainerHeight] = useState(600);
    const [models, setModels] = useState<Model[]>([]);
    const [filteredModels, setFilteredModels] = useState<Model[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [filters, setFilters] = useState<FilterState>({
      types: [],
      tags: [],
      priceRange: { min: 0, max: 100 },
    });
    const [sortOption, setSortOption] = useState<SortOption>('most-relevant');
    const [loadingState, setLoadingState] = useState<LoadingState>({
      isInitialLoading: true,
      isSearching: false,
      isFiltering: false,
      error: null,
    });

    // Add Model dialog state
    const [isAddModelOpen, setIsAddModelOpen] = useState(false);
    const openAddModel = useCallback(() => setIsAddModelOpen(true), []);
    const closeAddModel = useCallback(() => setIsAddModelOpen(false), []);

    // Close on Escape
    useEffect(() => {
      if (!isAddModelOpen) return undefined;
      const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          setIsAddModelOpen(false);
        }
      };
      window.addEventListener('keydown', onKey);
      return () => window.removeEventListener('keydown', onKey);
    }, [isAddModelOpen]);

    // Initial data loading with performance monitoring
    useEffect(() => {
      const loadInitialData = async () => {
        try {
          setLoadingState(prev => ({
            ...prev,
            isInitialLoading: true,
            error: null,
          }));
          const backendModels = await measureAsync(
            () => modelsService.getAvailableModels(),
            'initial-load'
          );
          // Map backend models to gallery Model type
          const mappedModels: Model[] = backendModels.map(m => ({
            id: m.id,
            name: m.name,
            version: '1.0',
            description: m.hfid || m.name,
            license: {
              type: 'Custom',
              icon: '/icons/model gallery/license_icon.svg',
            },
            tags: m.tags || [],
            type: m.modelType === 1 ? 'image' : 'text',
            pricing: {
              amount: Number((m.pricePer1M || 0).toFixed(4)),
              unit: 'per_1m_tokens',
              currency: 'USD',
            },
            capabilities: ['text-generation', 'conversation'],
            popularity: 50,
            isAvailable: true,
            metadata: { createdAt: new Date(), updatedAt: new Date() },
          }));
          setModels(mappedModels);
          setFilteredModels(mappedModels);
        } catch (error) {
          setLoadingState(prev => ({
            ...prev,
            error:
              error instanceof Error ? error.message : 'Failed to load models',
          }));
        } finally {
          setLoadingState(prev => ({ ...prev, isInitialLoading: false }));
        }
      };

      loadInitialData();
    }, [measureAsync]);

    // Container height measurement for virtual scrolling
    useEffect(() => {
      const updateContainerHeight = () => {
        if (containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect();
          setContainerHeight(
            Math.max(600, window.innerHeight - rect.top - 100)
          );
        }
      };

      updateContainerHeight();
      window.addEventListener('resize', updateContainerHeight);
      return () => window.removeEventListener('resize', updateContainerHeight);
    }, []);

    // Search and filter handler with performance monitoring
    const handleSearchAndFilter = useCallback(
      async (
        query: string,
        currentFilters: FilterState,
        currentSort: SortOption
      ) => {
        try {
          setLoadingState(prev => ({
            ...prev,
            isSearching: !!query,
            // Do not flip a separate filtering loading state to avoid flashing/closing dropdowns
            isFiltering: false,
            error: null,
          }));

          const operation = query ? 'search' : 'filter';
          // For now, perform simple filtering on the loaded list
          const lower = (query || '').toLowerCase();
          let results = models.filter(
            m =>
              !lower ||
              m.name.toLowerCase().includes(lower) ||
              m.description.toLowerCase().includes(lower)
          );
          // Filter by type tags
          if (currentFilters.types && currentFilters.types.length > 0) {
            results = results.filter(m =>
              currentFilters.types.includes(m.type)
            );
          }
          // Filter by tags
          if (currentFilters.tags && currentFilters.tags.length > 0) {
            results = results.filter(m =>
              currentFilters.tags.some(filterTag =>
                m.tags.some(
                  modelTag =>
                    modelTag.toLowerCase() === filterTag.toLowerCase() ||
                    modelTag.toLowerCase().includes(filterTag.toLowerCase())
                )
              )
            );
          }
          // Price filter (no real pricing yet)
          setFilteredModels(results);
        } catch (error) {
          setLoadingState(prev => ({
            ...prev,
            error:
              error instanceof Error
                ? error.message
                : 'Failed to search models',
          }));
        } finally {
          setLoadingState(prev => ({
            ...prev,
            isSearching: false,
            isFiltering: false,
          }));
        }
      },
      [models]
    );

    // Search handler
    const handleSearch = useCallback(
      (query: string) => {
        setSearchQuery(query);
        handleSearchAndFilter(query, filters, sortOption);
      },
      [filters, sortOption, handleSearchAndFilter]
    );

    // Filter handler
    const handleFiltersChange = useCallback(
      (newFilters: FilterState) => {
        setFilters(newFilters);
        handleSearchAndFilter(searchQuery, newFilters, sortOption);
      },
      [searchQuery, sortOption, handleSearchAndFilter]
    );

    // Sort handler
    const handleSortChange = useCallback(
      (newSort: SortOption) => {
        setSortOption(newSort);
        handleSearchAndFilter(searchQuery, filters, newSort);
      },
      [searchQuery, filters, handleSearchAndFilter]
    );

    // Chat navigation handler
    const handleGoToChat = useCallback(
      (modelId: string) => {
        try {
          // Find the model to check availability
          const model =
            filteredModels.find(m => m.id === modelId) ||
            models.find(m => m.id === modelId);

          if (!model) {
            setLoadingState(prev => ({
              ...prev,
              error: 'Model not found. Please try again.',
            }));
            return;
          }

          if (!model.isAvailable) {
            setLoadingState(prev => ({
              ...prev,
              error:
                'This model is currently unavailable. Please try another model.',
            }));
            return;
          }

          try {
            const destination =
              model.type === 'image' ? '/image-generation' : '/text-chat';
            navigate(destination, {
              state: {
                selectedModel: model.id,
                source: 'models-gallery',
              },
            });
          } catch {
            return;
          }
        } catch {
          setLoadingState(prev => ({
            ...prev,
            error: 'Failed to navigate to chat. Please try again.',
          }));
        }
      },
      [filteredModels, models, navigate]
    );

    // Error retry handler
    const handleRetry = useCallback(async () => {
      if (!models || models.length === 0) {
        try {
          setLoadingState(prev => ({
            ...prev,
            isInitialLoading: true,
            error: null,
          }));
          const backendModels = await modelsService.getAvailableModels();
          const allModels: Model[] = backendModels.map(m => ({
            id: m.id,
            name: m.name,
            version: '1.0',
            description: m.hfid || m.name,
            license: {
              type: 'Custom',
              icon: '/icons/model gallery/license_icon.svg',
            },
            tags: m.tags || [],
            type: m.modelType === 1 ? 'image' : 'text',
            pricing: {
              amount: Number((m.pricePer1M || 0).toFixed(4)),
              unit: 'per_1m_tokens',
              currency: 'USD',
            },
            capabilities: ['text-generation', 'conversation'],
            popularity: 50,
            isAvailable: true,
            metadata: { createdAt: new Date(), updatedAt: new Date() },
          }));
          setModels(allModels);
          setFilteredModels(allModels);
        } catch (error) {
          setLoadingState(prev => ({
            ...prev,
            error:
              error instanceof Error ? error.message : 'Failed to load models',
          }));
        } finally {
          setLoadingState(prev => ({ ...prev, isInitialLoading: false }));
        }
      } else {
        handleSearchAndFilter(searchQuery, filters, sortOption);
      }
    }, [models, searchQuery, filters, sortOption, handleSearchAndFilter]);

    // Keyboard navigation for model grid
    const handleGridKeyDown = useCallback(
      (event: React.KeyboardEvent) => {
        if (filteredModels.length === 0) return;

        const gridColumns = 4; // Assuming 4 columns in desktop layout
        const totalModels = filteredModels.length;
        let newIndex = focusedModelIndex;

        switch (event.key) {
          case 'ArrowRight':
            event.preventDefault();
            newIndex =
              focusedModelIndex < totalModels - 1
                ? focusedModelIndex + 1
                : focusedModelIndex;
            break;
          case 'ArrowLeft':
            event.preventDefault();
            newIndex =
              focusedModelIndex > 0 ? focusedModelIndex - 1 : focusedModelIndex;
            break;
          case 'ArrowDown':
            event.preventDefault();
            newIndex =
              focusedModelIndex + gridColumns < totalModels
                ? focusedModelIndex + gridColumns
                : focusedModelIndex;
            break;
          case 'ArrowUp':
            event.preventDefault();
            newIndex =
              focusedModelIndex - gridColumns >= 0
                ? focusedModelIndex - gridColumns
                : focusedModelIndex;
            break;
          case 'Home':
            event.preventDefault();
            newIndex = 0;
            break;
          case 'End':
            event.preventDefault();
            newIndex = totalModels - 1;
            break;
          case 'Enter':
          case ' ':
            event.preventDefault();
            if (focusedModelIndex >= 0 && focusedModelIndex < totalModels) {
              const model = filteredModels[focusedModelIndex];
              if (model.isAvailable) {
                handleGoToChat(model.id);
              }
            }
            break;
          default:
            return;
        }

        if (newIndex !== focusedModelIndex) {
          setFocusedModelIndex(newIndex);
          const modelCards =
            gridRef.current?.querySelectorAll('[role="gridcell"]');
          if (modelCards && modelCards[newIndex]) {
            (modelCards[newIndex] as HTMLElement).focus();
          }
        }
      },
      [filteredModels, focusedModelIndex, handleGoToChat]
    );

    useEffect(() => {
      setFocusedModelIndex(-1);
    }, [filteredModels]);

    const shouldUseVirtualScrolling =
      filteredModels && filteredModels.length > 50;
    const itemsPerRow = useMemo(() => {
      if (typeof window !== 'undefined') {
        const width = window.innerWidth;
        if (width < 768) return 1;
        if (width < 1024) return 2;
        return Math.floor((width - 64) / 320);
      }
      return 3;
    }, []);

    const virtualScrollConfig = useMemo(
      () => ({
        itemHeight: 400,
        containerHeight,
        itemsPerRow,
        itemWidth: 320,
        gap: 24,
        overscan: 2,
      }),
      [containerHeight, itemsPerRow]
    );

    const virtualScrolling = useVirtualScrollingGrid(
      shouldUseVirtualScrolling ? filteredModels : [],
      virtualScrollConfig
    );

    const MemoizedSearchBar = useMemo(
      () =>
        loadingState.isSearching ? (
          <SearchBarSkeleton />
        ) : (
          <SearchBar
            value={searchQuery}
            onChange={handleSearch}
            placeholder="Search models..."
            className={styles.searchBar}
          />
        ),
      [loadingState.isSearching, searchQuery, handleSearch]
    );

    const MemoizedFilterControls = useMemo(
      () => (
        <FilterControls
          filters={filters}
          onFiltersChange={handleFiltersChange}
          sortOption={sortOption}
          onSortChange={handleSortChange}
        />
      ),
      [filters, handleFiltersChange, sortOption, handleSortChange]
    );

    if (loadingState.isInitialLoading) {
      return (
        <div className={`${styles.page} ${className || ''}`}>
          <PageHeaderSkeleton />
          <SearchBarSkeleton />
          <div className={styles.filtersSection}>
            <div className={styles.filtersSkeleton}>
              <div className={styles.skeletonFilter} />
              <div className={styles.skeletonFilter} />
              <div className={styles.skeletonFilter} />
              <div className={styles.skeletonSort} />
            </div>
          </div>
          <div className={styles.modelsGrid}>
            {Array.from({ length: 6 }, (_, index) => (
              <ModelCardSkeleton key={index} />
            ))}
          </div>
        </div>
      );
    }

    if (loadingState.error) {
      return (
        <div className={`${styles.page} ${className || ''}`}>
          <div className={styles.errorState}>
            <h2>Something went wrong</h2>
            <p>{loadingState.error}</p>
            <button onClick={handleRetry} className={styles.retryButton}>
              Try Again
            </button>
          </div>
        </div>
      );
    }

    return (
      <ModelsGalleryErrorBoundary onRetry={handleRetry}>
        <div className={`${styles.page} ${className || ''}`}>
          <header className={styles.header} role="banner">
            <div className={styles.headerRow}>
              <h1 className={styles.title} id="page-title">
                Models Gallery
              </h1>
              <div className={styles.actions}>
                <button
                  type="button"
                  className={styles.addModelButton}
                  onClick={openAddModel}
                  aria-haspopup="dialog"
                  aria-controls="add-model-dialog"
                >
                  Add model
                </button>
              </div>
            </div>
            <div className={styles.blockchainStatus}>
              <div className={styles.connectedStatus}>
                <span className={styles.statusIcon}>🗄️</span>
                <span>Models loaded from backend</span>
              </div>
            </div>
          </header>

          <section
            className={styles.searchSection}
            aria-labelledby="search-heading"
            role="search"
          >
            <h2 id="search-heading" className="sr-only">
              Search Models
            </h2>
            {MemoizedSearchBar}
          </section>

          <section
            className={styles.filtersSection}
            aria-labelledby="filters-heading"
            role="region"
          >
            <h2 id="filters-heading" className="sr-only">
              Filter and Sort Options
            </h2>
            {MemoizedFilterControls}
          </section>

          <div className={styles.resultsSection}>
            <p className={styles.resultsCount} aria-live="polite">
              {filteredModels.length} model
              {filteredModels.length !== 1 ? 's' : ''} available
            </p>
          </div>

          <main
            id="main-content"
            className={styles.modelsGrid}
            role="main"
            aria-labelledby="models-heading"
            ref={containerRef}
          >
            <h2 id="models-heading" className="sr-only">
              Available Models
            </h2>
            {filteredModels.length === 0 ? (
              <div
                className={styles.emptyState}
                role="status"
                aria-live="polite"
                aria-atomic="true"
              >
                <h3>No models found</h3>
                <p>Try adjusting your search or filters</p>
              </div>
            ) : shouldUseVirtualScrolling ? (
              <div
                className={styles.virtualScrollContainer}
                style={{ height: containerHeight, overflow: 'auto' }}
              >
                <div
                  style={{
                    height: virtualScrolling.totalHeight,
                    position: 'relative',
                  }}
                >
                  <div
                    style={{
                      transform: `translateY(${virtualScrolling.offsetY}px)`,
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                    }}
                  >
                    {virtualScrolling.visibleItems.map((row, rowIndex) => (
                      <div
                        key={virtualScrolling.startIndex + rowIndex}
                        className={styles.virtualRow}
                        style={{
                          height: virtualScrollConfig.itemHeight,
                          display: 'flex',
                          gap: virtualScrollConfig.gap,
                        }}
                      >
                        {row.map((model, colIndex) => {
                          const globalIndex =
                            (virtualScrolling.startIndex + rowIndex) *
                              itemsPerRow +
                            colIndex;
                          return (
                            <ModelCard
                              key={model.id}
                              model={model}
                              onGoToChat={handleGoToChat}
                              className={styles.modelCard}
                              isFocused={globalIndex === focusedModelIndex}
                              onFocus={() => setFocusedModelIndex(globalIndex)}
                            />
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div
                ref={gridRef}
                className={styles.gridContainer}
                aria-label={`${filteredModels.length} models found`}
                aria-describedby="model-count grid-instructions"
                onKeyDown={handleGridKeyDown}
                tabIndex={0}
              >
                {filteredModels.map((model, index) => (
                  <ModelCard
                    key={model.id}
                    model={model}
                    onGoToChat={handleGoToChat}
                    className={styles.modelCard}
                    isFocused={index === focusedModelIndex}
                    onFocus={() => setFocusedModelIndex(index)}
                  />
                ))}
              </div>
            )}
          </main>

          {isAddModelOpen && (
            <div
              id="add-model-dialog"
              className={styles.modalOverlay}
              role="dialog"
              aria-modal="true"
              aria-labelledby="add-model-title"
              onClick={closeAddModel}
            >
              <div className={styles.modal} onClick={e => e.stopPropagation()}>
                <h2 id="add-model-title" className={styles.modalTitle}>
                  Add your model
                </h2>
                <p className={styles.modalText}>
                  To add your model please contact us on{' '}
                  <a href="https://t.me/swarmindCEO" target="_blank" rel="noopener noreferrer" className={styles.mailLink}>
                    Telegram
                  </a>
                </p>
                <div className={styles.modalActions}>
                  <button
                    type="button"
                    className={styles.closeButton}
                    onClick={closeAddModel}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          {loadingState.isSearching && (
            <div
              className={styles.loadingOverlay}
              role="status"
              aria-live="polite"
              aria-atomic="true"
              aria-label="Loading results"
            >
              <div className={styles.loadingSpinner} />
              <span className="sr-only">{'Searching models...'}</span>
            </div>
          )}

          <div id="grid-instructions" className="sr-only">
            Use arrow keys to navigate between models. Press Enter or Space to
            select a model. Press Home to go to first model, End to go to last
            model.
          </div>
        </div>
      </ModelsGalleryErrorBoundary>
    );
  }
);

export default ModelsGalleryPage;
